import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sprout, CookingPot, Wine, Phone, Mail, Images, X, ChevronLeft, ChevronRight } from "lucide-react";

const savoirFaire = [
  {
    icon: Sprout,
    titre: "L'élevage",
    desc: "Du printemps à l'automne, nos gros gris (Helix Aspersa Maxima) sont nourris avec une alimentation bio du Moulin Marion (01) et les herbes de notre parc.",
  },
  {
    icon: CookingPot,
    titre: "La préparation",
    desc: "Les escargots sont parés un à un, à la main, dans notre laboratoire : nous retirons l'appareil génital de chaque escargot. Nous dégermons chaque gousse d'ail et avons sélectionné notre excellent beurre pour sa finesse.",
  },
  {
    icon: Wine,
    titre: "Le court-bouillon",
    desc: "Nos escargots vont mijoter durant deux heures avec des légumes frais, du vin blanc des caves voisines et des épices choisies.",
  },
];

const categories = [
  {
    titre: "Recette traditionnelle",
    note: "Surgelé",
    items: [
      { nom: "Escargots en coquille", format: "La douzaine (x12)", prix: "8,50 €" },
      { nom: "Paquet de 6 douzaines en coquille", format: "Lot", prix: "49,00 €" },
      { nom: "Croq'Escargots", format: "La douzaine (x12)", prix: "8,50 €" },
      { nom: "Paquet de 6 douzaines de Croq'Escargots", format: "Lot", prix: "49,00 €" },
    ],
  },
  {
    titre: "Les Croq'Escargots au fromage",
    note: "Surgelé",
    items: [
      { nom: "Époisses", format: "La douzaine (x12)", prix: "9,00 €" },
      { nom: "Bleu", format: "La douzaine (x12)", prix: "9,00 €" },
      { nom: "Chèvre", format: "La douzaine (x12)", prix: "9,00 €" },
      { nom: "Kit apéro", format: "4 de chaque", prix: "9,00 €" },
    ],
  },
  {
    titre: "Les escargots à cuisiner",
    note: "",
    items: [
      { nom: "Bocal stérilisé en court-bouillon", format: "5 douzaines", prix: "24,00 €" },
      { nom: "Paquet surgelé en court-bouillon", format: "10 douzaines", prix: "41,00 €" },
      { nom: "Paquet surgelé en court-bouillon", format: "3 douzaines", prix: "12,30 €" },
      { nom: "Boîte de Croq'Escargots à remplir", format: "5 douzaines", prix: "9,00 €" },
      { nom: "Escargotine", format: "100 gr", prix: "9,00 €" },
    ],
  },
];

const images = [
  "https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/azgxztwr_WhatsApp%20Image%202026-07-02%20at%2010.19.14%20%289%29.jpeg",
  "https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/ud69aobp_cok%20crok%20a%20reduire%20%281%29.png",
  "https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/1o18stco_WhatsApp%20Image%202026-07-02%20at%2010.19.13%20%2812%29.jpeg",
  "https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/bqg7g6zj_WhatsApp%20Image%202026-07-02%20at%2010.19.13%20%2813%29.jpeg",
  "https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/mpcadbrc_from.jpg",
  "https://customer-assets.emergentagent.com/job_bourgogne-escargots/artifacts/9qcpg6cl_crok%20fromage.jpg",
];

const Produits = () => {
  const [lightbox, setLightbox] = useState(null); // index de l'image ou null

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const prev = (e) => { e.stopPropagation(); setLightbox((v) => (v - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setLightbox((v) => (v + 1) % images.length); };

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + images.length) % images.length);
      if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lightbox]);

  return (
    <section data-testid="produits-section" id="produits" className="py-24 lg:py-32 bg-creme">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <span className="text-sm uppercase tracking-[0.3em] text-mousse font-500">L'art de la transformation, fait maison</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-foreground mt-2 tracking-tight">
            Notre carte 2026
          </h2>
          <p className="text-lg text-foreground/75 mt-5 leading-relaxed">
            Des gros gris élevés, parés et cuisinés sur place, selon des recettes patiemment éprouvées.
            Des mets festifs prêts à savourer, aux escargots à cuisiner selon votre inspiration, vous
            savourerez toute la générosité de la Bourgogne dans chaque coquille.
          </p>
        </div>

        {/* Notre manière de faire */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {savoirFaire.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.titre}
                data-testid={`savoir-faire-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-sable rounded-2xl p-7 border border-border"
              >
                <div className="w-12 h-12 rounded-xl bg-mousse/12 text-mousse flex items-center justify-center">
                  <Icon size={24} />
                </div>
                <h3 className="font-heading text-2xl text-foreground mt-4">{s.titre}</h3>
                <p className="text-foreground/70 mt-2 leading-relaxed text-sm">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Images */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {images.map((img, i) => (
            <button
              key={img}
              type="button"
              data-testid={`gallery-image-${i}`}
              onClick={() => openLightbox(i)}
              className="rounded-2xl overflow-hidden h-44 sm:h-60 group relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-bordeaux"
            >
              <img src={img} alt="Escargots cuisinés Escarg'Holala" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute inset-0 bg-bordeaux/0 group-hover:bg-bordeaux/15 transition-colors duration-300" />
            </button>
          ))}
        </div>

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            data-testid="voir-tout-btn"
            onClick={() => openLightbox(0)}
            className="inline-flex items-center gap-2 rounded-full border border-mousse text-mousse font-600 px-6 py-3 hover:bg-mousse hover:text-white transition-all hover:-translate-y-0.5"
          >
            <Images size={18} /> Voir toutes les photos
          </button>
        </div>

        {/* Carte / tarifs */}
        <div className="grid lg:grid-cols-3 gap-8 mt-14">
          {categories.map((cat, ci) => (
            <motion.div
              key={cat.titre}
              data-testid={`categorie-${ci}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: ci * 0.1 }}
              className="bg-sable rounded-3xl p-7 shadow-[0_8px_30px_rgba(44,42,40,0.08)] border border-border"
            >
              <div className="flex items-baseline justify-between border-b border-border pb-4 mb-5">
                <h3 className="font-heading text-2xl text-bordeaux">{cat.titre}</h3>
                {cat.note && (
                  <span className="text-xs uppercase tracking-wide text-mousse font-600">{cat.note}</span>
                )}
              </div>
              <ul className="space-y-4">
                {cat.items.map((it, ii) => (
                  <li key={it.nom} data-testid={`item-${ci}-${ii}`} className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-600 text-foreground leading-snug">{it.nom}</div>
                      <div className="text-sm text-foreground/55">{it.format}</div>
                    </div>
                    <div className="font-heading text-xl text-bordeaux whitespace-nowrap">{it.prix}</div>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="tel:0662125192"
            data-testid="produits-call-btn"
            className="inline-flex items-center gap-2 rounded-full bg-bordeaux text-white font-600 px-7 py-3.5 hover:bg-bordeaux-dark transition-all hover:-translate-y-0.5"
          >
            <Phone size={18} /> Appeler pour commander
          </a>
          <button
            data-testid="produits-message-btn"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 rounded-full border border-bordeaux text-bordeaux font-600 px-7 py-3.5 hover:bg-bordeaux hover:text-white transition-all"
          >
            <Mail size={18} /> Commander par message
          </button>
        </div>

        <p className="text-center text-foreground/55 mt-8 text-sm">
          TVA non applicable, art. 293 B du CGI · Document non contractuel · Disponible en vente directe et sur nos événements.
        </p>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            data-testid="lightbox-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-[100] bg-foreground/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          >
            <button
              type="button"
              data-testid="lightbox-close"
              onClick={closeLightbox}
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
              aria-label="Fermer"
            >
              <X size={22} />
            </button>

            <button
              type="button"
              data-testid="lightbox-prev"
              onClick={prev}
              className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
              aria-label="Précédent"
            >
              <ChevronLeft size={24} />
            </button>

            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              src={images[lightbox]}
              alt="Escargots cuisinés Escarg'Holala"
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
            />

            <button
              type="button"
              data-testid="lightbox-next"
              onClick={next}
              className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
              aria-label="Suivant"
            >
              <ChevronRight size={24} />
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm font-500 tracking-wide">
              {lightbox + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Produits;
